"use client";

import { playerMarketingEmailsEnabled } from "@dailywars/core/marketing/preferences";
import { useForm } from "@tanstack/react-form";
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { parseError } from "evlog";
import { useCallback, useRef } from "react";

import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldTitle,
} from "#/components/ui/field";
import { Spinner } from "#/components/ui/spinner";
import { Switch } from "#/components/ui/switch";
import { patchMyPlayerProfileQuery } from "#/lib/queries";
import {
  updateMarketingEmailsSettingInputSchema,
  updateMyMarketingEmailsSetting,
} from "#/server/updateMyMarketingEmailsSetting";

import type { ProfileUserId } from "./-profile.types";

function MarketingEmailsSwitch({
  field,
}: {
  field: {
    state: { value: boolean; meta: { isValidating: boolean; errors: ReadonlyArray<unknown> } };
    handleChange: (value: boolean) => void;
  };
}) {
  const hasError = field.state.meta.errors.length > 0;
  return (
    <span className="flex shrink-0 items-center gap-2 self-center">
      {field.state.meta.isValidating ? <Spinner className="text-muted-foreground" /> : null}
      <Switch
        id="profile-marketingEmails"
        checked={field.state.value}
        disabled={field.state.meta.isValidating}
        onCheckedChange={(checked) => field.handleChange(checked)}
        aria-label="Product emails"
        aria-invalid={hasError}
      />
    </span>
  );
}

function useMarketingEmailsForm({
  userId,
  initialEnabled,
}: {
  userId: ProfileUserId;
  initialEnabled: boolean;
}) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const savedValueRef = useRef(initialEnabled);
  const form = useForm({ defaultValues: { enabled: initialEnabled } });

  const persistIfChanged = useCallback(
    async ({
      value,
      fieldApi,
    }: {
      value: boolean;
      fieldApi: { setValue: (value: boolean) => void };
    }) => {
      if (value === savedValueRef.current) return undefined;
      try {
        await updateMyMarketingEmailsSetting({ data: { enabled: value } });
        savedValueRef.current = value;
        patchMyPlayerProfileQuery({
          queryClient,
          userId,
          patch: { marketingEmailsEnabled: value },
        });
        void router.invalidate();
        return undefined;
      } catch (err) {
        fieldApi.setValue(savedValueRef.current);
        return parseError(err).message;
      }
    },
    [queryClient, router, userId],
  );

  return { form, persistIfChanged };
}

export function ProfileMarketingEmailsSetting({
  userId,
  profile,
}: {
  userId: ProfileUserId;
  profile: { marketingEmailsEnabled?: boolean };
}) {
  const { form, persistIfChanged } = useMarketingEmailsForm({
    userId,
    initialEnabled: playerMarketingEmailsEnabled(profile),
  });

  return (
    <form.Field
      name="enabled"
      validators={{
        onChange: updateMarketingEmailsSettingInputSchema.shape.enabled,
        onChangeAsync: persistIfChanged,
      }}
    >
      {(field) => {
        const hasError = field.state.meta.errors.length > 0;
        return (
          <Field
            orientation="horizontal"
            className="items-center justify-between gap-4"
            data-invalid={hasError}
          >
            <FieldContent className="min-w-0 flex-1">
              <FieldTitle>Product emails</FieldTitle>
              <FieldDescription>
                News and updates about Daily Wars. You can also unsubscribe from any email.
              </FieldDescription>
              {hasError ? (
                <FieldError
                  errors={field.state.meta.errors as Array<{ message?: string } | undefined>}
                />
              ) : null}
            </FieldContent>
            <MarketingEmailsSwitch field={field} />
          </Field>
        );
      }}
    </form.Field>
  );
}
